/**
 * RequirementVariableAccordion — in-panel Extracted Variables for one
 * checklist requirement. Scoped to REQUIREMENT_TO_FIELDS paths; reuses
 * shared SED variable row primitives (lineage, provenance, read-only probe).
 */
import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useStore } from '../../../store';
import { extractSEDVariables } from '../../../services/sedAmendments';
import type { SEDAmendment } from '../../../types/sedAmendments';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import type { SiteContext } from '../../../types/siteContext';
import type { SiteDocument } from '../../../types/siteRag';
import { variablePathsForRequirement, variablePathInRequirementScope } from '../../../config/requirementToFields';
import { useSedAmendments } from '../useSedAmendments';
import { SEDVariableRow, useDocLookup } from '../sedVariableShared';
import { VariableLineageDrawer } from '../VariableLineageDrawer';
import { DocViewerDialog } from '../DocViewerDialog';
import { toast } from 'sonner';

export interface RequirementVariableAccordionProps {
  requirementId: string;
  sed: SiteEvidenceDocument | null | undefined;
  siteContext?: SiteContext | null;
  documents?: SiteDocument[];
  siteId?: string;
  /** Deep-link highlight — auto-expands accordion + scrolls to row. */
  focusVariablePath?: string | null;
  /** Expand accordion without focusing a specific path. */
  forceExpanded?: boolean;
  onOpenDocument?: (docId: string) => void;
  onReloadSed?: () => void;
  /** When false, lineage probe only (no amend). Default true in unified panel. */
  enableLineageProbe?: boolean;
}

export const RequirementVariableAccordion: React.FC<RequirementVariableAccordionProps> = ({
  requirementId,
  sed,
  siteContext,
  documents,
  siteId,
  focusVariablePath,
  onOpenDocument,
  enableLineageProbe = true,
  forceExpanded = false,
}) => {
  const { amendmentState } = useStore();
  useSedAmendments(sed?.id ?? '');

  const [expanded, setExpanded] = React.useState(false);
  const [lineagePath, setLineagePath] = React.useState<string | null>(null);
  const [viewerOpenDoc, setViewerOpenDoc] = React.useState<SiteDocument | null>(null);
  const [viewerInitialPage, setViewerInitialPage] = React.useState<number | undefined>(undefined);
  const focusedRowRef = React.useRef<HTMLDivElement | null>(null);
  const focusRequestRef = React.useRef<{ key: string | null; scrolled: boolean }>({ key: null, scrolled: false });
  const focusRequestKey = focusVariablePath
    ? JSON.stringify([siteId ?? null, requirementId, focusVariablePath]) : null;

  const { docById, resolveDocName } = useDocLookup(documents);

  const openDocViewerAt = React.useCallback(
    (docId: string, pageNumber?: number) => {
      const d = docById.get(docId);
      if (!d) {
        toast?.('Source document is not available on this site.');
        return;
      }
      setViewerOpenDoc(d);
      setViewerInitialPage(typeof pageNumber === 'number' && pageNumber > 0 ? pageNumber : undefined);
    },
    [docById],
  );

  const scopedPaths = React.useMemo(
    () => variablePathsForRequirement(requirementId, sed ?? undefined),
    [requirementId, sed],
  );

  const variables = React.useMemo(() => {
    if (!sed) return [];
    const all = extractSEDVariables(
      sed,
      (amendmentState?.amendments ?? []) as SEDAmendment[],
      siteContext ?? null,
    );
    return all.filter(v => variablePathInRequirementScope(v.path, scopedPaths));
  }, [sed, amendmentState?.amendments, siteContext, scopedPaths]);

  // Auto-expand when deep-linked to a field path or forced open.
  React.useEffect(() => {
    if (focusVariablePath || forceExpanded) setExpanded(true);
  }, [focusVariablePath, forceExpanded]);

  // A focus request survives the first row arrival, but a saved-value reread
  // must not move the user's review position back to that already focused row.
  React.useEffect(() => {
    if (focusRequestRef.current.key !== focusRequestKey) {
      focusRequestRef.current = { key: focusRequestKey, scrolled: false };
    }
    const request = focusRequestRef.current;
    if (!focusRequestKey || !expanded || request.scrolled) return;
    const handle = window.setTimeout(() => {
      if (focusRequestRef.current !== request || request.scrolled) return;
      const el = focusedRowRef.current;
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        request.scrolled = true;
      } catch { /* best-effort */ }
    }, 200);
    return () => window.clearTimeout(handle);
  }, [focusRequestKey, variables, expanded]);

  if (!sed || variables.length === 0) {
    return (
      <section
        className="text-[10px] text-text-muted italic"
        data-testid="coverage-requirement-variables-empty"
      >
        No extracted variables registered for this requirement.
      </section>
    );
  }

  const populatedCount = variables.filter(v => v.value != null && v.value !== '').length;

  return (
    <section data-testid="coverage-requirement-variables">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 py-2 text-left border-t border-border-subtle"
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
        data-testid="coverage-requirement-variables-toggle"
      >
        <div className="flex items-center gap-1.5">
          {expanded ? (
            <ChevronDown className="w-3 h-3 shrink-0 text-text-muted" aria-hidden />
          ) : (
            <ChevronRight className="w-3 h-3 shrink-0 text-text-muted" aria-hidden />
          )}
          <span className="type-section text-text-secondary">
            All variables for this requirement
          </span>
        </div>
        <span className="type-body text-text-muted tabular-nums">
          {populatedCount}/{variables.length}
        </span>
      </button>

      {expanded && (
        <div
          className="divide-y divide-border-subtle/60 rounded-sm border border-border-subtle/60 bg-surface-2/20 px-2"
          data-testid="coverage-requirement-variables-list"
        >
          {variables.map(v => {
            const isFocused = focusVariablePath === v.path;
            const lineageExpanded = lineagePath === v.path;
            return (
              <React.Fragment key={v.path}>
                <SEDVariableRow
                  variable={v}
                  resolveDocName={resolveDocName}
                  openDocViewerAt={openDocViewerAt}
                  isFocused={isFocused}
                  rowRef={isFocused ? focusedRowRef : undefined}
                  showLineageToggle={enableLineageProbe}
                  showSourceBadge={false}
                  simpleSourceAction
                  lineageExpanded={lineageExpanded}
                  onToggleLineage={
                    enableLineageProbe
                      ? () => setLineagePath(prev => (prev === v.path ? null : v.path))
                      : undefined
                  }
                  compact
                />
                {lineageExpanded && (
                  <div
                    className="pb-2 pl-2"
                    data-testid={`coverage-panel-variable-lineage-drawer-${v.path}`}
                  >
                    <VariableLineageDrawer
                      variablePath={v.path}
                      label={v.label}
                      currentValue={v.value}
                      unit={v.unit}
                      sed={sed}
                      amendments={(amendmentState?.amendments ?? []) as SEDAmendment[]}
                      siteContext={siteContext ?? null}
                      documents={documents}
                      testIdSuffix={v.path}
                      onOpenDocument={onOpenDocument}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {viewerOpenDoc && (
        <DocViewerDialog
          doc={viewerOpenDoc}
          open={Boolean(viewerOpenDoc)}
          initialPage={viewerInitialPage}
          onClose={() => {
            setViewerOpenDoc(null);
            setViewerInitialPage(undefined);
          }}
        />
      )}
    </section>
  );
};
